<template>
	<div class="view-content min-h-1028">
		<div class="page-heading">
			<h1>Archived Leads Lookup</h1>
		</div>

        <row>
            <div class="flex flex-row justify-content-start gap-4">
                <div class="control-group">
                    <label class="control-label">Email</label>
                    <p-auto-complete
                        style="width: 100%"
                        id="archived-lead-email"
                        v-model="email"
                        @complete="search"
                        @input="validateEmail"
                        @focus="validateEmail"
                        :suggestions="matching_emails"
                    />
                    <gutter size="20px" />
                    <div class="flex gap-2">
                        <p-button 
                            icon="pi pi-check" 
                            label="Get Lead"

                            :disabled="search_disabled" 
                            class="px-2"
                            @click.prevent="submit()" 
                        />

                        <p-button
                            v-if="!search_disabled && retrieved_leads.length > 0"
                            icon="pi pi-download"
                            v-tooltip.top="'Download CSV'"
						    aria-label="Download"
                            @click="downloadCSV"
                        />

                    </div>

                </div>
                <div class="searching-spinner" v-if="loading">
                    <p-progress-spinner style="height: 30px; width: 30px" /> Searching archives...
                </div>
                <p-card class="api-key-card" v-if="retrieved_leads.length > 0 && !search_disabled" style="min-width: 50vw;">
                    <template #content>
                        <div class="flex flex-column gap-2">
                            <h3>Lead Details</h3>
                            <div v-for="(lead, index) in retrieved_leads" :key="lead.id" v-show="current_page === index">
                                    <ul>
                                        <li><strong>ID:</strong> {{ lead.id }}</li>
                                        <li><strong>Email:</strong> {{ lead.email }}</li>
                                        <li><strong>First Name:</strong> {{ lead.first_name }}</li>
                                        <li><strong>Last Name:</strong> {{ lead.last_name }}</li>
                                        <li><strong>Phone:</strong> {{ lead.phone }}</li>
                                        <li><strong>Address:</strong> {{ lead.address }}</li>
                                        <li><strong>City:</strong> {{ lead.city }}</li>
                                        <li><strong>State:</strong> {{ lead.state }}</li>
                                        <li><strong>Zip:</strong> {{ lead.zip }}</li>
                                        <li><strong>Found In Database:</strong> {{ lead.db }}</li>
                                        <li><strong>Created At:</strong> {{ new Date(lead.created_at).toLocaleString(undefined, { timeZoneName: 'short' }) }}</li>
                                        <li>
                                            <strong>All Data:</strong>
                                            <code-editor :disabled="true" v-model.parse="lead.lead_data" />
                                        </li>
                                    </ul>
                            </div>
                            <div class="flex flex-row gap-1" v-if="retrieved_leads.length > 1">
                                <p-button 
                                    icon="pi pi-chevron-left" 
                                    label="Previous" 
                                    :disabled="current_page === 0" 
                                    @click="current_page--" 
                                />
                                <p-button 
                                    icon="pi pi-chevron-right" 
                                    label="Next" 
                                    :disabled="current_page === retrieved_leads.length - 1" 
                                    @click="current_page++" 
                                />
                            </div>
                        </div>
                    </template>
                </p-card>
            </div>
        </row>
    </div>
</template>

<script>
import { getArchivedLead } from '@/lib/GQL/queries/archivedLead';
import { matchingArchivedLeadEmails } from '@/lib/GQL/queries/getMatchingArchivedLeadEmails';
import pCard from 'primevue/card';
import pInputText from 'primevue/inputtext';
import pProgressSpinner from 'primevue/progressspinner';
import pButton from 'primevue/button';
import pAutoComplete from 'primevue/autocomplete';
import { required, helpers } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import zod from 'zod';
import CodeEditor from '@/components/forms/CodeEditor.vue';

const zEmail = zod.string().email();

export default {
    name: 'DataRequests',
    components: {
        pCard,
        pInputText,
        pButton,
        pAutoComplete,
        pProgressSpinner,
    },
    inject: ['mq'],
    async setup() {
        return {
            v$: useVuelidate(),
        }
    },
    validations() {
        return {
            email: {
                required: helpers.withMessage('Email is required', required),
                email: helpers.withMessage('Email must be a valid email', email),
            }
        }
    },
    data() {
        return {
            email: '',
            matching_emails: [],
            search_disabled: true,
            retrieved_leads: [],
            loading: false,
            current_page: 0,
        };
    },
    methods: {
        validateEmail() {
            try {
                zEmail.parse(this.email);
                if (this.matching_emails.length > 0 && this.matching_emails.includes(this.email)) {
                    this.search_disabled = false;
                } else {
                    this.search_disabled = true;
                    this.retrieved_leads = [];
                }
            } catch (error) {
                this.search_disabled = true;
                this.retrieved_leads = [];
            }
        },
        onInput(event) {
            this.email = event.target.value;
            this.validateEmail();
        },
        async search(event) {
            const query = event.query;
            const res = await matchingArchivedLeadEmails(query);
            this.matching_emails = res.matchingArchivedLeadEmails;

            this.validateEmail();
        },
        downloadCSV() {
            const headers = [
                'id',
                'email',
                'first_name',
                'last_name',
                'phone',
                'address',
                'city',
                'state',
                'zip',
                'found_in_database',
                'created_at',
                'lead_data',
            ].join(',');

            const rows = this.retrieved_leads.map(lead => {
                return [
                    lead.id,
                    lead.email,
                    lead.first_name,
                    lead.last_name,
                    lead.phone,
                    lead.address,
                    lead.city,
                    lead.state,
                    lead.zip,
                    lead.db,
                    new Date(lead.created_at).toISOString(),
                    `"${JSON.stringify(lead.lead_data).replace(/"/g, '""')}"`, // Escape double quotes in JSON
                ].join(',')
            });

            const csv = [headers, ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `lead-${this.email}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        async submit() {
            try {
                this.loading = true;
                const res = await getArchivedLead(this.email);
                this.loading = false;
                this.retrieved_leads = res;
            } catch (error) {
                this.loading = false;
                this.retrieved_leads = [];
                this.$toast.add({
                    severity: 'error',
                    summary: `${error}: Unable to retrieve lead. Please try again later.`,
                    life: 10000,
                });
            }
        }
    }
}
</script>

<style>
	.searching-spinner {
		align-items: center;
		color: var(--gray-35);
		display: flex;
		font-size: 0.875rem;
		gap: 10px;
		margin-bottom: 2rem;
	}
</style>